import { cn } from '@/lib/utils';

// Mock Income-Tax PAN card (designer: sc-workflow-preview__id-card).
// `compact` shrinks the PAN number (designer's scanner-frame rule) so it stays
// on one line inside the narrower OCR scan frame.
export function IdCardMock({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className="relative flex w-full flex-col rounded-xl p-[10px]"
      style={{ background: 'var(--pan-card-gradient)' }}
    >
      <div className="mb-3 flex justify-between text-[10px] font-bold text-[#374151]">
        <span>INCOME TAX DEPARTMENT</span>
        <span>GOVT. OF INDIA</span>
      </div>
      {/* `min-w-0` on the row and the details column is what keeps the QR inside the
          card. The detail rows are `whitespace-nowrap` (so "Father's Name:" never
          splits from its value), and a nowrap child of a flex item refuses to shrink
          below its content width unless min-width is explicitly released — without
          this the column pushed the QR straight out through the card's right edge. */}
      <div className="relative z-10 flex min-w-0 flex-1 items-start gap-3">
        <div className="h-full w-[54px] shrink-0 overflow-hidden rounded-md border border-white bg-[#94A3B8]">
          <img src="/pan-card-photo.png" alt="" className="h-full w-full object-cover" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div
            className={cn(
              'mb-1.5 whitespace-nowrap font-extrabold leading-none text-[#1E3A8A]',
              compact
                ? 'text-[length:var(--text-16)] tracking-tight'
                : 'text-[length:var(--text-18)] tracking-[1.5px]',
            )}
          >
            ABCDE1234F
          </div>
          {[
            ['Name:', 'Smith Jhon'],
            ["Father's Name:", 'David Jhon'],
            ['Date Of Birth:', '10/05/1980'],
          ].map(([label, val]) => (
            // `whitespace-nowrap` keeps each label and its value on one line. Without
            // it they wrapped as soon as the card got narrow — "Father's Name:" over
            // one line and "David Jhon" under it — which read as a broken card rather
            // than a small one. A mock ID has fixed, known content, so holding it on
            // one line is safe; there is no user text here that could overflow.
            <div
              key={label}
              className="mb-1 flex flex-row items-center gap-1 overflow-hidden whitespace-nowrap"
            >
              <span className="text-[9px] font-medium leading-none text-[#4B5563]">{label}</span>
              <span className="text-[9px] font-bold leading-none text-[#111827]">{val}</span>
            </div>
          ))}
        </div>
        <div className="h-[60px] w-[60px] shrink-0 rounded-md bg-white p-1">
          <img src="/pan-card-qr.png" alt="" className="h-full w-full object-cover" />
        </div>
      </div>
      <div className="relative z-10 mt-6">
        <div className="flex flex-row items-center gap-2">
          <span className="text-[9px] font-medium text-[#111827]">Card Holder's Signature</span>
          <div className="mb-0.5 h-3 w-[40px] border-b border-[#111827]" />
        </div>
      </div>
    </div>
  );
}
